import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'
import { getBedbankDisplayPricing } from 'lib/hotels/bedbankPricing'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import type { OfferBestPriceData } from 'selectors/offerSelectors'

interface Params {
  bestPrices: Record<string, OfferBestPriceData>
  offers: Array<App.HotelOffer | App.BedbankOffer>
  bedbankOfferRates: App.OfferState['bedbankOfferRates']
  filters: App.OfferListFilters
}

/**
 * Overlay dated LPP member/public totals onto calendar similar-offer prices.
 * `getOffersBestPrices` keeps bedbank `bestMemberPrice` at 0 for search map / YMAL.
 */
export function applyCalendarSimilarBedbankDisplayPrices({
  bestPrices,
  offers,
  bedbankOfferRates,
  filters,
}: Params): Record<string, OfferBestPriceData> {
  if (!filters.checkIn || !filters.checkOut || !filters.rooms) {
    return bestPrices
  }

  const rateKey = getBedbankRateKey(filters.rooms, filters.checkIn, filters.checkOut)
  let changed = false
  const nextPrices = { ...bestPrices }

  for (const offer of offers) {
    if (!isBedbankOffer(offer)) {
      continue
    }

    const current = nextPrices[offer.id]
    if (!current) {
      continue
    }

    const lowestRate = bedbankOfferRates[offer.id]?.[rateKey]?.[0]
    if (!lowestRate) {
      continue
    }

    const displayPricing = getBedbankDisplayPricing(lowestRate)
    const bestPrice = displayPricing.total
    const bestMemberPrice = displayPricing.memberTotal ?? 0
    const bestBaseMemberPrice = displayPricing.memberPrice ?? 0
    const bestValue = displayPricing.value
    const duration = lowestRate.nights
    if (
      current.bestPrice === bestPrice &&
      current.bestMemberPrice === bestMemberPrice &&
      current.bestBaseMemberPrice === bestBaseMemberPrice &&
      current.bestValue === bestValue &&
      current.duration === duration
    ) {
      continue
    }

    nextPrices[offer.id] = {
      ...current,
      bestPrice,
      bestMemberPrice,
      bestBaseMemberPrice,
      bestValue,
      duration,
    }
    changed = true
  }

  return changed ? nextPrices : bestPrices
}
